import React from 'react';
import { MusicIcon, UsersIcon } from 'lucide-react';
import { ArchLines } from './ArchLines';
const steps = [{
  label: 'Step 1',
  name: 'Intention',
  body: 'Pick the focus of the day — vitality, connection, creativity, encounter or calm. One line that shapes everything after it.',
  dot: 'bg-patrol-mustard'
}, {
  label: 'Step 2',
  name: 'Rise',
  body: 'The opening circle, walks and shared rhythm that bring people into the room and into the group.',
  dot: 'bg-ember'
}, {
  label: 'Step 3',
  name: 'Deepen',
  body: 'Slower, closer moments — encounter, integration, stillness — each with a gentle version for newcomers and a fuller one for regulars.',
  dot: 'bg-moss'
}, {
  label: 'Step 4',
  name: 'Return',
  body: 'Energy comes back, then the closing circle with a short sharing, so people leave grounded and want next week.',
  dot: 'bg-patrol-teal'
}];
export function ProductReveal() {
  return <section aria-labelledby="mechanism-heading" className="topo-dark relative w-full overflow-hidden border-y border-forest-line bg-forest" data-id="element-196" data-mp="1b0xm2ro4jr64">
      <ArchLines className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 text-paper/[0.06]" data-id="element-197" data-mp="1f0zpb59icsni" />
      <div className="relative mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-198" data-mp="1jtsrbldldmvw">
        <div className="text-center" data-id="element-199" data-mp="1orat8rt6z1ue">
          <p className="eyebrow text-ember" data-id="element-200" data-mp="1ougj499na7r6">The mechanism</p>
          <h2 id="mechanism-heading" className="trail-underline mt-3 font-display text-[30px] leading-tight text-paper sm:text-5xl" data-id="element-201" data-mp="s34w0pylnbq5">
            The Rise-Deepen-Return Map
          </h2>
          <p className="field-note mt-5 text-[28px] leading-tight text-paper sm:text-3xl" data-id="element-202" data-mp="1116sqtbavi7m">
            Open the plan. Pick your music. Hold the group.
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-base text-paper/80 sm:text-lg" data-id="element-203" data-mp="2ciq1103qmbd1">
            All 84 plans follow the same four-step map, so you always know
            where the class is going. Each one is built for 90 minutes, with a
            line to scale it to 75 or 120 — and it’s the whole class, not a
            list of exercises you still have to sequence.
          </p>
        </div>

        <ol className="relative mx-auto mt-9 max-w-2xl" data-id="element-204" data-mp="21rbmggax8eqz">
          <span aria-hidden="true" className="absolute bottom-6 left-[19px] top-6 border-l-2 border-dashed border-paper/25" data-id="element-205" data-mp="21opgum7lc9be" />
          {steps.map((step, i) => <li key={step.name} className="relative flex gap-4 pb-4 last:pb-0" data-id="element-206" data-mp="1901pqa05uppx" data-mpi={i}>
              <span className={`relative z-10 flex h-10 w-10 flex-none items-center justify-center rounded-full font-display text-sm text-pine ring-4 ring-forest ${step.dot}`} data-id="element-207" data-mp="1454jfau9mugq">
                {i + 1}
              </span>
              <div className="flex-1 rounded-xl border border-paper/10 bg-paper/[0.06] px-4 py-3" data-id="element-208" data-mp="qbd57p5lameo">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3" data-id="element-209" data-mp="ldbmj5c2sp3q">
                  <span className="font-display text-lg text-paper" data-id="element-210" data-mp="2x0f56h4jfjvj">
                    {step.name}
                  </span>
                  <span className="text-[11px] font-bold uppercase tracking-widest text-ember" data-id="element-211" data-mp="31rx7p9m7lsim">
                    {step.label}
                  </span>
                </div>
                <p className="mt-1 text-sm leading-snug text-paper/80" data-id="element-212" data-mp="2s0j34nfei2im">
                  {step.body}
                </p>
              </div>
            </li>)}
        </ol>

        <div className="mx-auto mt-6 grid max-w-2xl gap-3 sm:grid-cols-2" data-id="element-213" data-mp="3kppzbfrdug0p">
          <div className="rounded-xl bg-moss-soft px-4 py-4 text-pine" data-id="element-214" data-mp="3knnct5pfv50q">
            <span className="flex items-center gap-2 font-display text-base" data-id="element-215" data-mp="36p6wcn1ry05e">
              <MusicIcon className="h-5 w-5 text-moss-deep" aria-hidden="true" data-id="element-216" data-mp="3aqseonx2v6mz" />
              Music by function
            </span>
            <p className="mt-1.5 text-sm leading-snug text-pine-soft" data-id="element-217" data-mp="1v98j06zkcryd">
              Every phase comes with a music brief — tempo, BPM range and
              character — so you fill it from your own library in about ten
              minutes.
            </p>
          </div>
          <div className="rounded-xl bg-sky-soft px-4 py-4 text-pine" data-id="element-218" data-mp="2arrx9iqpk4c7">
            <span className="flex items-center gap-2 font-display text-base" data-id="element-219" data-mp="25tglh2gurjrc">
              <UsersIcon className="h-5 w-5 text-sky" aria-hidden="true" data-id="element-220" data-mp="25rdlbplzka8w" />
              Two depths, one plan
            </span>
            <p className="mt-1.5 text-sm leading-snug text-pine-soft" data-id="element-221" data-mp="2yixarr96ekjc">
              A newcomer version and a deepening version in every plan, plus
              a no-contact path and a seated option wherever they’re needed.
            </p>
          </div>
        </div>
      </div>
    </section>;
}